import React, { useState, useEffect } from "react";
import ValuationSidebarSuperVisor from "../../components/supervisor/valuationSidebarSuperVisor";
import { useParams } from "react-router-dom";
import PropertyMap from "../map/PropertyMap";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, X, Download, UserPlus, CheckCircle, } from "lucide-react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

// Updated TypeScript interfaces
interface Coordinates {
  latitude: number;
  longitude: number;
}

interface Location {
  province?: string;
  district: string;
  sector: string;
  cell: string;
  village: string;
  streetAddress?: string;
  coordinates?: Coordinates;
}

interface LandParcel {
  area: number;
  landUse: string;
  slopeCategory: string;
  accessToRoad: boolean;
}

interface Building {
  constructedArea: number;
  constructionYear: number;
  numberOfFloors: number;
  constructionQuality: string;
  exteriorFinish: string;
  roofType: string;
  maintenanceCondition: string;
  bedrooms?: number;
  bathrooms?: number;
  floorCondition?: string;
  wallsCondition?: string;
  windowsCondition?: string;
  doorsCondition?: string;
  roofCondition?: string;
}

interface Amenities {
  hasElectricity: boolean;
  hasWater: boolean;
  hasSewage: boolean;
  hasInternet: boolean;
  hasFence: boolean;
  hasGarden: boolean;
  hasParkingSpace: boolean;
  hasAnnexBuilding?: boolean;
  hasOutsideKitchen?: boolean;
  hasPavedParking?: boolean;
  hasRetainingWalls?: boolean;
  hasWaterTank?: boolean;
  hasVeranda?: boolean;
}

interface BuildingComponents {
  outsideKitchenDescription?: string;
  waterTankDescription?: string;
  landscapingDescription?: string;
  soilDrainageDescription?: string;
  pvcPipesDescription?: string;
  washTapsDescription?: string;
  fixturesFittingsDescription?: string;
}

interface ValuationDetails {
  status: "New" | "Pending" | "Completed";
  landValue: number;
  buildingValue: number;
  amenitiesValue: number;
  depreciationValue: number;
  totalPropertyValue: number;
  valuationDate: string;
  collectorNotes?: string;
  approvedBy?: string | null;
  approvedDate?: string | null;
  assignedCollector?: string;
  // Enhanced valuation fields
  landRate?: number;
  mainHouseArea?: number;
  mainHouseRate?: number;
  annexHouseArea?: number;
  annexRate?: number;
  pavedArea?: number;
  pavedAreaRate?: number;
  gardenArea?: number;
  gateFenceLength?: number;
  retainingWallsLength?: number;
  fenceRate?: number;
  retainingWallRate?: number;
  entranceGateRate?: number;
  septicTankValue?: number;
  landscapingValue?: number;
  forcedSaleDiscount?: number;
  maintenanceCoefficient?: number;
  annualDepreciationRate?: number;
  originalCostOfConstruction?: number;
  depreciatedValue?: number;
  presentValue?: number;
}

interface Property {
  id: string;
  upi?: string;
  ownerName: string;
  ownerIdNumber: string;
  phone?: string;
  plotNumber?: string;
  location: Location;
  landParcel?: LandParcel;
  mainBuilding?: Building;
  amenities?: Amenities;
  buildingComponents?: BuildingComponents;
  valuationDetails?: ValuationDetails;
  adjustmentsAndVariables: any;
  buildingDescription: any;
  propertyDetails: any;
  propertyPhotos: any;
  // Image fields
  frontView?: string;
  backView?: string;
  leftView?: string;
  rightView?: string;
  livingRoom?: string;
  kitchen?: string;
  bathroom?: string;
  bedroom?: string;
  diningRoom?: string;
  veranda?: string;
  googleEarthOne?: string;
  googleEarthTwo?: string;
  masterPlanPhoto?: string;
  photo?: string;
}

interface Collector {
  id: string;
  lastName: string;
  firstName?: string;
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  isVerified: boolean;
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string;
  profileImageUrl?: string | null;
}

interface AuthContextType {
  currentUser: {
    name: string;
    role: string;
  };
}

// Mock auth context
const useAuth = (): AuthContextType => {
  return {
    currentUser: {
      name: "Supervisor Name",
      role: "supervisor",
    },
  };
};

// Base URL for API
const BASE_URL = "https://valuate-api.vercel.app/api/v1";

// Default Property object
const defaultProperty: Property = {
  id: "",
  ownerName: "",
  ownerIdNumber: "",
  location: {
    district: "",
    sector: "",
    cell: "",
    village: "",
  },
  upi: "",
  landParcel: {
    area: 0,
    landUse: "",
    slopeCategory: "",
    accessToRoad: false,
  },
  mainBuilding: {
    constructedArea: 0,
    constructionYear: 0,
    numberOfFloors: 0,
    constructionQuality: "",
    exteriorFinish: "",
    roofType: "",
    maintenanceCondition: "",
  },
  amenities: {
    hasElectricity: false,
    hasWater: false,
    hasSewage: false,
    hasInternet: false,
    hasFence: false,
    hasGarden: false,
    hasParkingSpace: false,
  },
  buildingComponents: {},
  adjustmentsAndVariables: {},
  buildingDescription: {},
  propertyDetails: {},
  propertyPhotos: [],
  valuationDetails: {
    status: "Pending",
    landValue: 0,
    buildingValue: 0,
    amenitiesValue: 0,
    depreciationValue: 0,
    totalPropertyValue: 0,
    valuationDate: "",
    collectorNotes: "",
    approvedBy: null,
    approvedDate: null,
    assignedCollector: "",
  },
};

// Helper function to get all property images
const getAllPropertyImages = (
  property: Property
): Array<{ url: string; label: string }> => {
  const images: Array<{ url: string; label: string }> = [];

  // Check propertyPhotos array first
  if (property.propertyPhotos && Array.isArray(property.propertyPhotos)) {
    property.propertyPhotos.forEach((photo: any) => {
      if (photo.url) {
        images.push({
          url: photo.url,
          label: photo.label || `Property Image ${images.length + 1}`,
        });
      }
    });
  }

  // Check individual image fields
  const imageFields = [
    { field: property.frontView, label: "Front View" },
    { field: property.backView, label: "Back View" },
    { field: property.leftView, label: "Left View" },
    { field: property.rightView, label: "Right View" },
    { field: property.livingRoom, label: "Living Room" },
    { field: property.kitchen, label: "Kitchen" },
    { field: property.bathroom, label: "Bathroom" },
    { field: property.bedroom, label: "Bedroom" },
    { field: property.diningRoom, label: "Dining Room" },
    { field: property.veranda, label: "Veranda" },
    { field: property.googleEarthOne, label: "Google Earth One" },
    { field: property.googleEarthTwo, label: "Google Earth Two" },
    { field: property.masterPlanPhoto, label: "Master Plan" },
    { field: property.photo, label: "Property Photo" },
  ];

  imageFields.forEach(({ field, label }) => {
    if (field) {
      images.push({ url: field, label });
    }
  });

  return images;
};

// Simplified PDF Generator (you can keep your existing detailed version)
// Simplified PDF Generator (you can keep your existing detailed version)
// Enhanced PDF Generator with Photos & Beautiful Styling
const generatePDFReport = async (property: Property) => {
  try {
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    let yPos = margin;

    // Load logo as base64
    const loadImage = (url: string): Promise<string> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx?.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/png", 0.8));
        };
        img.onerror = () => resolve(""); // fallback
        img.src = url + "?not-from-cache-please";
      });
    };

    // Try to load a logo (you can replace with your actual logo)
    const logoUrl = "https://img.icons8.com/color/96/000000/building.png";
    const logoBase64 = await loadImage(logoUrl);

    // Helper: Add colored header
    const addHeader = () => {
      doc.setFillColor(0, 102, 204); // Rwanda Blue
      doc.rect(0, 0, pageWidth, 22, "F");

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text(
        "PROPERTY VALUATION REPORT",
        pageWidth / 2,
        10,
        { align: "center" }
      );

      if (logoBase64) {
        doc.addImage(logoBase64, "PNG", margin, 5, 15, 15);
      }

      doc.setFontSize(8);
      doc.setFont("helvetica", "italic");
      doc.text("Generated by Rwanda Property Valuation System", pageWidth / 2, 18, {
        align: "center",
      });
    };

    // Helper: Section title
    const addSectionTitle = (title: string) => {
      yPos += 8;
      doc.setFillColor(0, 102, 204);
      doc.rect(margin - 5, yPos - 6, pageWidth - 2 * margin + 10, 10, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(title.toUpperCase(), margin, yPos);
      yPos += 10;
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
    };

    // Helper: Normal text
    const addText = (text: string, indent = 0, bold = false) => {
      doc.setFont("helvetica", bold ? "bold" : "normal");
      const splitText = doc.splitTextToSize(
        text,
        pageWidth - 2 * margin - indent
      );
      doc.text(splitText, margin + indent, yPos);
      yPos += splitText.length * 5.5;
    };

    // Helper: Add key-value pair
    const addKeyValue = (key: string, value: any, indent = 0) => {
      const formattedKey = `${key}:`;
      const formattedValue = String(value || "N/A");

      doc.setFont("helvetica", "bold");
      const keyWidth = doc.getTextWidth(formattedKey);
      doc.text(formattedKey, margin + indent, yPos);

      doc.setFont("helvetica", "normal");
      doc.text(formattedValue, margin + indent + keyWidth + 2, yPos);
      yPos += 6;
    };

    // === Start Building PDF ===
    addHeader();

    // Property Summary Card
    yPos = 30;
    doc.setFillColor(245, 250, 255);
    doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 35, 5, 5, "F");
    
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text(`Property ID: ${property.id || "N/A"}`, margin + 8, yPos + 8);
    
    // Status Badge
    // const status = property.valuationDetails?.status || "Pending";
    // const statusColor = status === "Completed"
    //   ? [40, 167, 69]
    //   : status === "Pending"
    //   ? [255, 193, 7]
    //   : [108, 117, 125];
    // doc.setFillColor(...statusColor);
    // doc.roundedRect(pageWidth - margin - 60, yPos + 5, 50, 10, 3, 3, "F");
    // doc.setTextColor(255, 255, 255);
    // doc.setFontSize(10);
    // doc.text(status.toUpperCase(), pageWidth - margin - 35, yPos + 11, {
    //   align: "center",
    // });

    yPos += 15;
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");
    addText(`UPI: ${property.upi || "Not Assigned"}`, 8);
    addText(`Owner: ${property.ownerName || "N/A"}`, 8);
    yPos += 5;

    // === Location ===
    addSectionTitle("Location Details");
    autoTable(doc, {
      startY: yPos,
      head: [["Field", "Value"]],
      body: [
        ["District", property.location?.district || "N/A"],
        ["Sector", property.location?.sector || "N/A"],
        ["Cell", property.location?.cell || "N/A"],
        ["Village", property.location?.village || "N/A"],
        ["Street Address", property.location?.streetAddress || "N/A"],
        [
          "Coordinates",
          property.location?.coordinates
            ? `${property.location.coordinates.latitude}, ${property.location.coordinates.longitude}`
            : "N/A",
        ],
      ],
      theme: "grid",
      headStyles: { fillColor: [0, 102, 204], textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [240, 248, 255] },
      columnStyles: { 0: { fontStyle: "bold", cellWidth: 50 } },
      margin: { left: margin, right: margin },
    });
    yPos = (doc as any).lastAutoTable.finalY + 10;

    // === Owner Information ===
    addSectionTitle("Owner Information");
    autoTable(doc, {
      startY: yPos,
      head: [["Parameter", "Details"]],
      body: [
        ["Owner Name", property.ownerName || "N/A"],
        ["ID Number", property.ownerIdNumber || "N/A"],
        ["UPI", property.upi || "N/A"],
        ["Plot Number", property.plotNumber || "N/A"],
      ],
      theme: "grid",
      headStyles: { fillColor: [255, 193, 7], textColor: 0 },
      alternateRowStyles: { fillColor: [255, 253, 231] },
      margin: { left: margin, right: margin },
    });
    yPos = (doc as any).lastAutoTable.finalY + 10;

    // === Land Details ===
    if (property.landParcel) {
      addSectionTitle("Land Details");
      autoTable(doc, {
        startY: yPos,
        head: [["Parameter", "Value"]],
        body: [
          ["Land Area", `${property.landParcel.area || 0} m²`],
          ["Land Use", property.landParcel.landUse || "N/A"],
          ["Slope Category", property.landParcel.slopeCategory || "N/A"],
          ["Road Access", property.landParcel.accessToRoad ? "Yes" : "No"],
        ],
        theme: "grid",
        headStyles: { fillColor: [46, 125, 50], textColor: 255 },
        alternateRowStyles: { fillColor: [230, 255, 230] },
        margin: { left: margin, right: margin },
      });
      yPos = (doc as any).lastAutoTable.finalY + 20
    }

    // === Building Details ===
    if (property.mainBuilding) {
      
      addSectionTitle("Building Details");
      autoTable(doc, {
        startY: yPos,
        head: [["Parameter", "Value"]],
        body: [
          ["Constructed Area", `${property.mainBuilding.constructedArea || 0} m²`],
          ["Construction Year", property.mainBuilding.constructionYear || "N/A"],
          ["Number of Floors", property.mainBuilding.numberOfFloors || 0],
          ["Construction Quality", property.mainBuilding.constructionQuality || "N/A"],
          ["Exterior Finish", property.mainBuilding.exteriorFinish || "N/A"],
          ["Roof Type", property.mainBuilding.roofType || "N/A"],
          ["Maintenance Condition", property.mainBuilding.maintenanceCondition || "N/A"],
        ],
        theme: "striped",
        headStyles: { fillColor: [101, 31, 255], textColor: 255 },
        alternateRowStyles: { fillColor: [240, 235, 255] },
        margin: { left: margin, right: margin },
      });
      yPos = (doc as any).lastAutoTable.finalY + 10;
    }

    // === Amenities ===
    if (property.amenities) {
      addSectionTitle("Amenities & Features");
      const amenitiesList = [
        ["Electricity", property.amenities.hasElectricity ? "✓ Yes" : "✗ No"],
        ["Water Supply", property.amenities.hasWater ? "✓ Yes" : "✗ No"],
        ["Sewage System", property.amenities.hasSewage ? "✓ Yes" : "✗ No"],
        ["Internet", property.amenities.hasInternet ? "✓ Yes" : "✗ No"],
        ["Fence/Wall", property.amenities.hasFence ? "✓ Yes" : "✗ No"],
        ["Garden", property.amenities.hasGarden ? "✓ Yes" : "✗ No"],
        ["Parking Space", property.amenities.hasParkingSpace ? "✓ Yes" : "✗ No"],
      ];
      
      autoTable(doc, {
        startY: yPos,
        head: [["Amenity", "Available"]],
        body: amenitiesList,
        theme: "grid",
        headStyles: { fillColor: [255, 87, 34], textColor: 255 },
        alternateRowStyles: { fillColor: [255, 245, 242] },
        columnStyles: { 1: { halign: "center" } },
        margin: { left: margin, right: margin },
      });
      yPos = (doc as any).lastAutoTable.finalY + 10;
    }

    // === Valuation Details ===
    if (property.valuationDetails) {
      doc.addPage();
      addHeader();
      yPos = 30;

      addSectionTitle("Valuation Summary");
      
      // Total Value in large font
      doc.setFontSize(24);
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "bold");
      doc.text(
        `RWF ${property.valuationDetails.totalPropertyValue.toLocaleString()}`,
        pageWidth / 2,
        yPos + 20,
        { align: "center" }
      );
      yPos += 25;
      doc.setFontSize(12);
      doc.setTextColor(80, 80, 80);
      doc.text("Total Estimated Property Value", pageWidth / 2, yPos, {
        align: "center",
      });
      yPos += 20;

      // Valuation breakdown table
      autoTable(doc, {
        startY: yPos,
        head: [["Component", "Amount (RWF)"]],
        body: [
          ["Land Value", property.valuationDetails.landValue.toLocaleString()],
          ["Building Value", property.valuationDetails.buildingValue.toLocaleString()],
          ["Amenities Value", property.valuationDetails.amenitiesValue.toLocaleString()],
          ["Depreciation", `-${property.valuationDetails.depreciationValue.toLocaleString()}`],
          [
            { content: "TOTAL VALUE", fontStyle: "bold", textColor: [0, 102, 204] },
            {
              content: property.valuationDetails.totalPropertyValue.toLocaleString(),
              fontStyle: "bold",
              textColor: [0, 102, 204],
            },
          ],
        ],
        theme: "striped",
        headStyles: { fillColor: [46, 125, 50], textColor: 255 },
        styles: { fontSize: 12 },
        columnStyles: { 1: { halign: "right", fontStyle: "bold" } },
        alternateRowStyles: { fillColor: [230, 255, 230] },
        margin: { left: margin, right: margin },
      });
      yPos = (doc as any).lastAutoTable.finalY + 15;

      // Additional valuation info
      addSectionTitle("Valuation Details");
      autoTable(doc, {
        startY: yPos,
        head: [["Detail", "Value"]],
        body: [
          ["Valuation Date", property.valuationDetails.valuationDate ? new Date(property.valuationDetails.valuationDate).toLocaleDateString() : "N/A"],
          ["Status", property.valuationDetails.status || "Pending"],
          ["Assigned Collector", property.valuationDetails.assignedCollector || "N/A"],
          ["Approved By", property.valuationDetails.approvedBy || "N/A"],
          ["Approval Date", property.valuationDetails.approvedDate ? new Date(property.valuationDetails.approvedDate).toLocaleDateString() : "N/A"],
        ],
        theme: "grid",
        headStyles: { fillColor: [128, 0, 128], textColor: 255 },
        alternateRowStyles: { fillColor: [248, 240, 255] },
        margin: { left: margin, right: margin },
      });
      yPos = (doc as any).lastAutoTable.finalY + 10;

      // Collector Notes
      if (property.valuationDetails.collectorNotes) {
        yPos += 5;
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        doc.text("Collector Notes:", margin, yPos);
        yPos += 5;
        doc.setFont("helvetica", "normal");
        const notesLines = doc.splitTextToSize(
          property.valuationDetails.collectorNotes,
          pageWidth - 2 * margin
        );
        doc.text(notesLines, margin, yPos);
        yPos += notesLines.length * 6;
      }
    }

    // === Property Photos ===
    const propertyImages = getAllPropertyImages(property);
    if (propertyImages.length > 0) {
      doc.addPage();
      addHeader();
      yPos = 30;
      
      addSectionTitle("Property Photographs");
      
      // Add photo count
      doc.setFontSize(11);
      doc.setTextColor(100, 100, 100);
      doc.text(`Total Images: ${propertyImages.length}`, margin, yPos);
      yPos += 8;

      // Create a grid of photos (max 4 per page)
      const imgWidth = 85;
      const imgHeight = 60;
      const cols = 2;
      const rows = 3; // Max 6 images per page
      const spacingX = (pageWidth - 2 * margin - cols * imgWidth) / (cols - 1);
      const spacingY = 75;

      let imagesAdded = 0;
      let pageCount = 1;

      for (let i = 0; i < propertyImages.length; i++) {
        const photo = propertyImages[i];
        const row = Math.floor((i % (cols * rows)) / cols);
        const col = (i % (cols * rows)) % cols;
        const x = margin + col * (imgWidth + spacingX);
        const y = yPos + row * spacingY;

        // Check if we need a new page
        if (i > 0 && i % (cols * rows) === 0) {
          doc.addPage();
          addHeader();
          yPos = 30;
          doc.setFontSize(14);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(0, 0, 0);
          doc.text(`Property Photographs (Page ${++pageCount})`, margin, yPos);
          yPos += 15;
        }

        try {
          const base64 = await loadImage(photo.url);
          if (base64) {
            // Add photo frame
            doc.setDrawColor(200, 200, 200);
            doc.setFillColor(250, 250, 250);
            doc.roundedRect(x - 2, y - 2, imgWidth + 4, imgHeight + 15, 5, 5, "FD");
            
            // Add image
            doc.addImage(base64, "JPEG", x, y, imgWidth, imgHeight);
            
            // Add label
            doc.setFontSize(9);
            doc.setTextColor(100, 100, 100);
            const label = photo.label.length > 25 ? photo.label.substring(0, 22) + "..." : photo.label;
            doc.text(label, x + imgWidth / 2, y + imgHeight + 8, { align: "center" });
            
            imagesAdded++;
          }
        } catch (err) {
          console.error(`Failed to load image ${i}:`, err);
          // Draw placeholder
          doc.setDrawColor(200, 200, 200);
          doc.setFillColor(240, 240, 240);
          doc.roundedRect(x, y, imgWidth, imgHeight, 3, 3, "FD");
          doc.setFontSize(8);
          doc.setTextColor(150, 150, 150);
          doc.text("Image not available", x + imgWidth / 2, y + imgHeight / 2, {
            align: "center",
          });
        }
      }

      // Add summary of images
      doc.addPage();
      addHeader();
      yPos = 30;
      
      addSectionTitle("Image Summary");
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      doc.text(`Total Images Captured: ${propertyImages.length}`, margin, yPos);
      yPos += 8;
      doc.text(`Images Successfully Included: ${imagesAdded}`, margin, yPos);
      
      // List all images
      yPos += 15;
      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text("Image List:", margin, yPos);
      yPos += 5;
      
      propertyImages.forEach((image, index) => {
        if (yPos > pageHeight - 20) {
          doc.addPage();
          yPos = margin + 10;
        }
        doc.setFont("helvetica", "normal");
        doc.text(`${index + 1}. ${image.label}`, margin + 5, yPos);
        yPos += 5;
      });
    }

    // === Additional Property Data ===
    doc.addPage();
    addHeader();
    yPos = 30;

    addSectionTitle("Additional Property Information");

    // Building Components
    if (property.buildingComponents) {
      yPos += 5;
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      doc.text("Building Components:", margin, yPos);
      yPos += 6;
      
      const components = [
        ["Bedrooms", property.buildingComponents.numberOfBedrooms || 0],
        ["Bathrooms", property.buildingComponents.numberOfBathrooms || 0],
        ["Living Rooms", property.buildingComponents.numberOfLivingRooms || 0],
        ["Kitchens", property.buildingComponents.numberOfKitchens || 0],
        ["Toilets", property.buildingComponents.numberOfToilets || 0],
        ["Storeys", property.buildingComponents.numberOfStoreys || 0],
      ];
      
      autoTable(doc, {
        startY: yPos,
        head: [["Component", "Count"]],
        body: components,
        theme: "plain",
        styles: { fontSize: 10 },
        columnStyles: { 0: { fontStyle: "bold" }, 1: { halign: "center" } },
        margin: { left: margin, right: margin },
      });
      yPos = (doc as any).lastAutoTable.finalY + 10;
    }

    // Property Details
    if (property.propertyDetails) {
      yPos += 5;
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      doc.text("Property Characteristics:", margin, yPos);
      yPos += 6;
      
      const characteristics = [
        ["Plot Dimensions", property.propertyDetails.plotDimensions || "N/A"],
        ["Plot Shape", property.propertyDetails.plotShape || "N/A"],
        ["Soil Type", property.propertyDetails.soilType || "N/A"],
        ["Drainage", property.propertyDetails.drainageCondition || "N/A"],
        ["Topography", property.propertyDetails.topography || "N/A"],
        ["View Quality", property.propertyDetails.viewQuality || "N/A"],
      ];
      
      autoTable(doc, {
        startY: yPos,
        head: [["Characteristic", "Value"]],
        body: characteristics,
        theme: "plain",
        styles: { fontSize: 10 },
        columnStyles: { 0: { fontStyle: "bold" } },
        margin: { left: margin, right: margin },
      });
      yPos = (doc as any).lastAutoTable.finalY + 10;
    }

    // Footer on all pages
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.setTextColor(150, 150, 150);
      
      // Bottom left: Generation info
      doc.text(
        `Generated on ${new Date().toLocaleDateString("en-GB", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })} at ${new Date().toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
        })}`,
        margin,
        pageHeight - 15
      );
      
      // Bottom center: Confidential
      doc.text("CONFIDENTIAL", pageWidth / 2, pageHeight - 15, { align: "center" });
      
      // Bottom right: Page numbers
      doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin - 30, pageHeight - 15);
      
      // Add decorative line
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, pageHeight - 20, pageWidth - margin, pageHeight - 20);
    }

    // Save the PDF
    const fileName = `Property_Valuation_Report_${
      property.upi || property.id
    }_${new Date().toISOString().split("T")[0]}.pdf`;
    doc.save(fileName);

    return true;
  } catch (error) {
    console.error("Error generating PDF report:", error);
    alert("Failed to generate PDF report. Please try again.");
    return false;
  }
};

// PropertyDetails Component
interface PropertyDetailsProps {
  supervisorMode?: boolean;
}

const PropertyDetails: React.FC<PropertyDetailsProps> = ({
  supervisorMode = false,
}) => {
  const { id: propertyId } = useParams<{ id: string }>();
  const { currentUser } = useAuth();

  // State management
  const [property, setProperty] = useState<Property>(defaultProperty);
  const [collectors, setCollectors] = useState<Collector[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isCollectorModalOpen, setIsCollectorModalOpen] = useState(false);
  const [selectedCollector, setSelectedCollector] = useState<string>("");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [zoomImage, setZoomImage] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = 4;

  // Image gallery states
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);

  // Get all property images
  const propertyImages = getAllPropertyImages(property);
  const hasImages = propertyImages.length > 0;

  // Navigation functions
  const nextPage = () => setCurrentPage((p) => Math.min(p + 1, totalPages));
  const prevPage = () => setCurrentPage((p) => Math.max(p - 1, 1));

  // Image gallery handlers
  const nextImage = () => {
    setSelectedImageIndex((prev) => (prev + 1) % propertyImages.length);
    setZoomLevel(1);
  };

  const prevImage = () => {
    setSelectedImageIndex(
      (prev) => (prev - 1 + propertyImages.length) % propertyImages.length
    );
    setZoomLevel(1);
  };

  const openLightbox = (index: number) => {
    setSelectedImageIndex(index);
    setIsLightboxOpen(true);
    setZoomLevel(1);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
    setZoomLevel(1);
  };

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.25, 0.5));
  };

  // Fetch property data from API
  useEffect(() => {
    const fetchProperty = async () => {
      if (!propertyId) return;
      setIsLoading(true);
      setErrorMessage("");
      try {
        const Valuation_token = localStorage.getItem("valuation_token");
        const response = await fetch(`${BASE_URL}/properties/${propertyId}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Valuation_token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch property: ${response.statusText}`);
        }

        const result = await response.json();
        setProperty(result.data);
      } catch (error) {
        setErrorMessage("Failed to load property details. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchProperty();
  }, [propertyId, isAssigning]);
  /* ================= UPLOAD REPORT ================= */
  const token = localStorage.getItem("valuation_token");
  // Fetch collectors from API
  useEffect(() => {
    const fetchCollectors = async () => {
      try {
        const response = await fetch(`${BASE_URL}/users`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch collectors: ${response.statusText}`);
        }

        const result = await response.json();
        const users: User[] = result.data || [];
        const dataCollectors = users.filter(
          (user) => user.role === "DATA_COLLECTOR"
        );
        const collectorArray: Collector[] = dataCollectors.map((user) => ({
          id: user.id,
          lastName: user.lastName,
          firstName: user.firstName,
        }));
        setCollectors(collectorArray);
      } catch (error) {
        console.error("Error fetching collectors:", error);
      }
    };
    fetchCollectors();
  }, []);

  // Handler for generate report button
  const handleGenerateReport = async () => {
    if (!property || !property.id) {
      alert("No property data available to generate report.");
      return;
    }
    setIsGeneratingPDF(true);
    try {
      await generatePDFReport(property);
    } catch (error) {
      console.error("Error generating report:", error);
      alert("Failed to generate report. Please try again.");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Handle opening the collector modal
  const handleOpenCollectorModal = () => {
    setIsCollectorModalOpen(true);
    setSearchQuery("");
  };

  // Filter collectors based on search query
  const filteredCollectors = collectors.filter((collector) =>
    collector.lastName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle closing the collector modal
  const handleCloseCollectorModal = () => {
    setIsCollectorModalOpen(false);
    setSelectedCollector("");
    setSearchQuery("");
  };

  // Handle assigning a collector
  const handleAssignCollector = async () => {
    if (!selectedCollector || !propertyId) return;
    setIsAssigning(true);
    setErrorMessage("");
    try {
      const Valuation_token = localStorage.getItem("valuation_token");
      const collector = collectors.find((c) => c.id === selectedCollector);
      const response = await fetch(`${BASE_URL}/properties/${propertyId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Valuation_token}`,
        },
        body: JSON.stringify({
          valuationDetails: {
            ...property.valuationDetails,
            assignedCollector: collector
              ? `${collector.firstName} ${collector.lastName}`
              : "",
            collectorId: selectedCollector,
          },
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to assign collector");
      }

      const result = await response.json();
      console.log("Assign collector result:", result);
      setProperty(result.data);
      setSuccessMessage("Collector assigned successfully!");
      setIsCollectorModalOpen(false);
      setSelectedCollector("");
      setSearchQuery("");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error assigning collector:", error);
      setErrorMessage("Failed to assign collector. Please try again.");
    } finally {
      setIsAssigning(false);
    }
  };

  // Handle supervisor approval
  const handleApprove = async () => {
    if (!currentUser || !propertyId) return;
    setIsApproving(true);
    setErrorMessage("");
    try {
      const Valuation_token = localStorage.getItem("valuation_token");
      const response = await fetch(`${BASE_URL}/properties/${propertyId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Valuation_token}`,
        },
        body: JSON.stringify({
          valuationDetails: {
            ...property.valuationDetails,
            status: "Completed",
            approvedBy: currentUser.name,
            approvedDate: new Date().toISOString(),
          },
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to approve valuation");
      }

      const result = await response.json();
      console.log("Approval result:", result);
      setProperty(result.data);
      setSuccessMessage("Valuation approved successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error approving valuation:", error);
      setErrorMessage("Failed to approve valuation. Please try again.");
    } finally {
      setIsApproving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto p-6 bg-gray-50 min-h-screen">
        <div className="bg-white rounded-lg shadow-sm p-6 text-center py-20">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          <p className="mt-4 text-gray-600">Loading property details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 bg-gray-50 min-h-screen">
      {/* Lightbox Modal */}
      {isLightboxOpen && hasImages && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90">
          <div className="relative w-full h-full flex flex-col items-center justify-center">
            {/* Close button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-10 text-white p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <X size={24} />
            </button>
            {/* Zoom controls */}
            <div className="absolute top-4 left-4 z-10 flex items-center space-x-2">
              <button
                onClick={handleZoomOut}
                disabled={zoomLevel <= 0.5}
                className="text-white p-2 hover:bg-white/10 rounded-full disabled:opacity-50 transition-colors"
              >
                <ZoomOut size={20} />
              </button>
              <span className="text-white text-sm">
                {(zoomLevel * 100).toFixed(0)}%
              </span>
              <button
                onClick={handleZoomIn}
                disabled={zoomLevel >= 3}
                className="text-white p-2 hover:bg-white/10 rounded-full disabled:opacity-50 transition-colors"
              >
                <ZoomIn size={20} />
              </button>
            </div>
            {/* Navigation buttons */}
            <button
              onClick={prevImage}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 text-white p-3 hover:bg-white/10 rounded-full transition-colors"
            >
              <ChevronLeft size={24} />
            </button>
            {/* Main image */}
            <div className="max-w-4xl max-h-[80vh] overflow-auto">
              <img
                src={propertyImages[selectedImageIndex].url}
                alt={propertyImages[selectedImageIndex].label}
                className="transition-transform duration-200"
                style={{ transform: `scale(${zoomLevel})` }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src =
                    "https://via.placeholder.com/800x600/cccccc/969696?text=Image+Not+Found";
                }}
              />
            </div>
            <button
              onClick={nextImage}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 text-white p-3 hover:bg-white/10 rounded-full transition-colors"
            >
              <ChevronRight size={24} />
            </button>
            {/* Image label */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-lg font-medium">
              {propertyImages[selectedImageIndex].label}
            </div>
            {/* Thumbnails */}
            <div className="absolute bottom-4 left-0 right-0 px-8">
              <div className="flex overflow-x-auto space-x-2 justify-center py-2">
                {propertyImages.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setSelectedImageIndex(index);
                      setZoomLevel(1);
                    }}
                    className={`flex-shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition-all ${
                      index === selectedImageIndex
                        ? "border-blue-500"
                        : "border-transparent hover:border-gray-300"
                    }`}
                  >
                    <img
                      src={image.url}
                      alt={image.label}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
            {/* Image counter */}
            <div className="absolute top-4 right-16 text-white text-sm">
              {selectedImageIndex + 1} / {propertyImages.length}
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">
            Property Details
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            UPI: {property?.upi || "Not assigned"}
          </p>
        </div>
        <div className="flex space-x-3">
          {/* Generate Report Button */}
          <button
            onClick={handleGenerateReport}
            disabled={isGeneratingPDF || !property}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGeneratingPDF ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Generating Report...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-1.5" />
                Generate Report
              </>
            )}
          </button>
          <button
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            onClick={handleOpenCollectorModal}
            disabled={isAssigning}
          >
            {isAssigning ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Assigning...
              </>
            ) : (
              "Assign Collector"
            )}
          </button>
          {/* Collector Assignment Modal */}
          {isCollectorModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-600 bg-opacity-50 p-4">
              <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md relative">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Assign Collector
                  </h3>
                  <button
                    onClick={handleCloseCollectorModal}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <svg
                      className="h-6 w-6"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search Collector
                  </label>
                  <input
                    type="text"
                    className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Search by name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="mb-4 max-h-64 overflow-y-auto">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Collector
                  </label>
                  {filteredCollectors.length > 0 ? (
                    <ul className="border border-gray-300 rounded-md divide-y divide-gray-200">
                      {filteredCollectors.map((collector) => (
                        <li
                          key={collector.id}
                          className={`px-4 py-2 cursor-pointer hover:bg-indigo-50 ${
                            selectedCollector === collector.id
                              ? "bg-indigo-100"
                              : ""
                          }`}
                          onClick={() => setSelectedCollector(collector.id)}
                        >
                          {collector.firstName} {collector.lastName}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-gray-500">No collectors found</p>
                  )}
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={handleCloseCollectorModal}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAssignCollector}
                    disabled={!selectedCollector || isAssigning}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {isAssigning ? "Assigning..." : "Assign"}
                  </button>
                </div>
              </div>
            </div>
          )}
          {supervisorMode &&
            property.valuationDetails?.status === "Pending" && (
              <button
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50"
                onClick={handleApprove}
                disabled={isApproving}
              >
                {isApproving ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Approving...
                  </>
                ) : (
                  "Approve Valuation"
                )}
              </button>
            )}
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-green-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Property Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="col-span-3 lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm p-6">
            {/* Property Header with Image */}
            <div className="flex flex-col md:flex-row justify-between items-start mb-6">
              <div className="mb-4 md:mb-0">
                <h2 className="text-xl font-medium text-gray-800">
                  {property?.ownerName || "Unnamed Property"}
                </h2>
                <div className="flex items-center mt-2 space-x-2">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      property.valuationDetails?.status === "Completed"
                        ? "bg-green-100 text-green-800"
                        : property.valuationDetails?.status === "Pending"
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {property.valuationDetails?.status || "New"}
                  </span>
                  {property.valuationDetails?.assignedCollector && (
                    <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Assigned to: {property.valuationDetails.assignedCollector}
                    </span>
                  )}
                </div>
              </div>
              {/* Main Property Image with Click to Open Lightbox */}
              {hasImages ? (
                <div className="relative">
                  <button
                    onClick={() => openLightbox(0)}
                    className="w-32 h-32 rounded-lg overflow-hidden border-2 border-gray-200 group relative focus:outline-none z-10"
                  >
                    <img
                      src={propertyImages[0].url}
                      alt={propertyImages[0].label}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center">
                      <span className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                        View All Images
                      </span>
                    </div>
                  </button>
                  <div className="text-xs text-gray-500 mt-1 text-center">
                    {propertyImages.length} images available
                  </div>
                </div>
              ) : (
                <div className="w-32 h-32 rounded-lg bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400">
                  <div className="text-center">
                    <div className="text-2xl mb-1">📷</div>
                    <span className="text-xs">No Images</span>
                  </div>
                </div>
              )}
            </div>

            {/* Image Gallery Sidebar - Always visible on Page 1 */}
            {hasImages && currentPage === 1 && (
              <div className="mb-6 pb-6 border-b border-gray-200">
                <h3 className="text-base font-medium text-gray-800 mb-3">
                  Property Images
                </h3>
                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                  {propertyImages.slice(0, 16).map((image, index) => (
                    <button
                      key={index}
                      onClick={() => openLightbox(index)}
                      className={`aspect-square rounded overflow-hidden border-2 transition-all hover:border-blue-400 ${
                        index === selectedImageIndex
                          ? "border-blue-500"
                          : "border-gray-200"
                      }`}
                    >
                      <img
                        src={image.url}
                        alt={image.label}
                        className="w-full h-full object-cover hover:scale-110 transition-transform duration-200"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src =
                            "https://via.placeholder.com/100/cccccc/969696?text=Image";
                        }}
                      />
                    </button>
                  ))}
                  {propertyImages.length > 16 && (
                    <button
                      onClick={() => openLightbox(0)}
                      className="aspect-square rounded bg-gray-50 border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-500 hover:border-blue-300 hover:text-blue-500 transition-colors"
                    >
                      <span className="text-xl">+</span>
                      <span className="text-xs mt-1">
                        {propertyImages.length - 16} more
                      </span>
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Page Content */}
            <div className="min-h-[400px]">
              {/* Page 1: Owner & Location Information */}
              {currentPage === 1 && (
                <div className="space-y-6">
                  <section>
                    <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                      <span className="mr-2">👤</span> Basic Property
                      Information
                    </h2>
                    <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-gray-600">Full Name</span>
                        <p className="font-medium">{property.ownerName}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">ID Number</span>
                        <p className="font-medium">{property.ownerIdNumber}</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">
                          Phone Number
                        </span>
                        <p className="font-medium">
                          {property.phone || "Not provided"}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">
                          Plot Number
                        </span>
                        <p className="font-medium">
                          {property.plotNumber || "Not assigned"}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">
                          UPI Number
                        </span>
                        <p className="font-medium">
                          {property.upi || "Not assigned"}
                        </p>
                      </div>
                    </div>
                  </section>
                  <section>
                    <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                      <span className="mr-2">📍</span> Location
                    </h2>
                    <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <span className="text-sm text-gray-600">Province</span>
                        <p className="font-medium">
                          {property.location?.province || "Not specified"}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">District</span>
                        <p className="font-medium">
                          {property.location?.district || "Not specified"}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Sector</span>
                        <p className="font-medium">
                          {property.location?.sector || "Not specified"}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Cell</span>
                        <p className="font-medium">
                          {property.location?.cell || "Not specified"}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Village</span>
                        <p className="font-medium">
                          {property.location?.village || "Not specified"}
                        </p>
                      </div>
                    </div>
                  </section>
                  <section>
                    {property.location?.coordinates && (
                      <div className="md:col-span-2">
                        <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                          <span className="mr-2"></span> Coordinates
                        </h2>
                        <p className="font-medium ml-6">
                          {property.location.coordinates.latitude},{" "}
                          {property.location.coordinates.longitude}
                        </p>
                      </div>
                    )}
                    {property.location?.streetAddress && (
                      <div className="md:col-span-2">
                        <span className="text-sm text-gray-600">
                          Street Address
                        </span>
                        <p className="font-medium">
                          {property.location.streetAddress}
                        </p>
                      </div>
                    )}
                  </section>
                  {/* Valuation Parameters */}
                  <section>
                    <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                      <span className="mr-2">📐</span> Valuation Parameters
                    </h2>
                    <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Land Use */}
                      <div>
                        <label className="text-sm font-medium text-gray-700">
                          Land Use
                        </label>
                        <p className="p-2 bg-white border rounded">
                          {property.landParcel?.landUse || "N/A"}
                        </p>
                      </div>
                      {/* Slope Category */}
                      <div>
                        <label className="text-sm font-medium text-gray-700">
                          Slope Category
                        </label>
                        <p className="p-2 bg-white border rounded">
                          {property.landParcel?.slopeCategory || "N/A"}
                        </p>
                      </div>
                      {/* Land Area */}
                      <div>
                        <label className="text-sm text-gray-600">
                          Land Area (m²)
                        </label>
                        <p className="font-medium">
                          {property.landParcel?.area || 0}
                        </p>
                      </div>
                      {/* Land Rate */}
                      <div>
                        <label className="text-sm text-gray-600">
                          Land Rate (per m²)
                        </label>
                        <p className="font-medium">
                          {property.valuationDetails?.landRate || 0}
                        </p>
                      </div>
                      {/* Land Value */}
                      <div>
                        <label className="text-sm text-gray-600">
                          Land Value (RWF)
                        </label>
                        <p className="font-medium">
                          {property.valuationDetails?.landValue || 0}
                        </p>
                      </div>
                      {/* Main House Area */}
                      <div>
                        <label className="text-sm text-gray-600">
                          Main House Area (m²)
                        </label>
                        <p className="font-medium">
                          {property.valuationDetails?.mainHouseArea || 0}
                        </p>
                      </div>
                      {/* Main House Rate */}
                      <div>
                        <label className="text-sm text-gray-600">
                          Main House Rate (per m²)
                        </label>
                        <p className="font-medium">
                          {property.valuationDetails?.mainHouseRate || 0}
                        </p>
                      </div>
                      {/* Annex Area */}
                      <div>
                        <label className="text-sm text-gray-600">
                          Annex House Area (m²)
                        </label>
                        <p className="font-medium">
                          {property.valuationDetails?.annexHouseArea || 0}
                        </p>
                      </div>
                      {/* Annex Rate */}
                      <div>
                        <label className="text-sm text-gray-600">
                          Annex Rate (per m²)
                        </label>
                        <p className="font-medium">
                          {property.valuationDetails?.annexRate || 0}
                        </p>
                      </div>
                      {/* Fence Length */}
                      <div>
                        <label className="text-sm text-gray-600">
                          Fence Length (m)
                        </label>
                        <p className="font-medium">
                          {property.valuationDetails?.gateFenceLength || 0}
                        </p>
                      </div>
                      {/* Fence Rate */}
                      <div>
                        <label className="text-sm text-gray-600">
                          Fence Rate (per m)
                        </label>
                        <p className="font-medium">
                          {property.valuationDetails?.fenceRate || 0}
                        </p>
                      </div>
                      {/* Retaining Walls */}
                      <div>
                        <label className="text-sm text-gray-600">
                          Retaining Walls Length (m)
                        </label>
                        <p className="font-medium">
                          {property.valuationDetails?.retainingWallsLength || 0}
                        </p>
                      </div>
                      {/* Retaining Wall Rate */}
                      <div>
                        <label className="text-sm text-gray-600">
                          Retaining Wall Rate (per m)
                        </label>
                        <p className="font-medium">
                          {property.valuationDetails?.retainingWallRate || 0}
                        </p>
                      </div>
                      {/* Entrance Gate Length */}
                      <div>
                        <label className="text-sm text-gray-600">
                          Entrance Gate Length (m)
                        </label>
                        <p className="font-medium">
                          {property.valuationDetails?.gateFenceLength || 0}
                        </p>
                      </div>
                      {/* Gate Rate */}
                      <div>
                        <label className="text-sm text-gray-600">
                          Entrance Gate Rate (per m)
                        </label>
                        <p className="font-medium">
                          {property.valuationDetails?.entranceGateRate || 0}
                        </p>
                      </div>
                      {/* Paved Area */}
                      <div>
                        <label className="text-sm text-gray-600">
                          Paved Area (m²)
                        </label>
                        <p className="font-medium">
                          {property.valuationDetails?.pavedArea || 0}
                        </p>
                      </div>
                      {/* Paved Rate */}
                      <div>
                        <label className="text-sm text-gray-600">
                          Paved Area Rate (per m²)
                        </label>
                        <p className="font-medium">
                          {property.valuationDetails?.pavedAreaRate || 0}
                        </p>
                      </div>
                      {/* Septic Tank */}
                      <div>
                        <label className="text-sm text-gray-600">
                          Septic Tank Number
                        </label>
                        <p className="font-medium">
                          {property.valuationDetails?.septicTankValue || 0}
                        </p>
                      </div>
                      {/* Septic Tank Rate */}
                      <div>
                        <label className="text-sm text-gray-600">
                          Septic Tank Value (per unit)
                        </label>
                        <p className="font-medium">
                          {property.valuationDetails?.septicTankValue || 0}
                        </p>
                      </div>
                      {/* Garden Area */}
                      <div>
                        <label className="text-sm text-gray-600">
                          Garden Area (m²)
                        </label>
                        <p className="font-medium">
                          {property.valuationDetails?.gardenArea || 0}
                        </p>
                      </div>
                      {/* Garden Rate */}
                      <div>
                        <label className="text-sm text-gray-600">
                          Garden Rate (per m²)
                        </label>
                        <p className="font-medium">
                          {property.valuationDetails?.entranceGateRate || 0}
                        </p>
                      </div>
                      {/* Verandah Area */}
                      <div>
                        <label className="text-sm text-gray-600">
                          Verandah Area (m²)
                        </label>
                        <p className="font-medium">
                          {property.valuationDetails?.pavedArea || 0}
                        </p>
                      </div>
                      {/* Verandah Rate */}
                      <div>
                        <label className="text-sm text-gray-600">
                          Verandah Rate (per m²)
                        </label>
                        <p className="font-medium">
                          {property.valuationDetails?.pavedAreaRate || 0}
                        </p>
                      </div>
                      {/* Landscaping Value */}
                      <div>
                        <label className="text-sm text-gray-600">
                          Landscaping Value (RWF)
                        </label>
                        <p className="font-medium">
                          {property.valuationDetails?.landscapingValue || 0}
                        </p>
                      </div>
                      {/* Forced Sale Discount */}
                      <div>
                        <label className="text-sm text-gray-600">
                          Forced Sale Discount (%)
                        </label>
                        <p className="font-medium">
                          {property.valuationDetails?.forcedSaleDiscount || 0}
                        </p>
                      </div>
                      {/* Depreciation Rate */}
                      <div>
                        <label className="text-sm text-gray-600">
                          Depreciation Rate (%)
                        </label>
                        <p className="font-medium">
                          {property.valuationDetails?.annualDepreciationRate ||
                            0}
                        </p>
                      </div>
                    </div>
                  </section>
                </div>
              )}

              {/* Page 3: Building Components */}
              {currentPage === 2 &&
                property.buildingComponents &&
                property.valuationDetails && (
                  <div className="space-y-6">
                    {/* Amenities */}
                    <section>
                      <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                        <span className="mr-2">✨</span> Amenities
                      </h2>
                      {property.amenitiesData && (
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {Object.entries(property.amenitiesData).map(
                              ([key, value]) => (
                                <div key={key} className="flex items-center space-x-2">
                                  <div
                                    className={`w-3 h-3 rounded-full ${
                                      value ? "bg-green-500" : "bg-gray-300"
                                    }`}
                                  />
                                  <span className="text-sm capitalize">
                                    {key
                                      .replace("has", "")
                                      .replace(/([A-Z])/g, " $1")
                                      .trim()}
                                  </span>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}
                    </section>
                    {/* Building Components */}
                    <section>
                      <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                        <span className="mr-2">🔧</span> Building Components
                      </h2>
                      <div className="space-y-3">
                        {[
                          {
                            label: "Outside Kitchen",
                            value:
                              property.buildingComponents?.outsideKitchenDescription,
                          },
                          {
                            label: "Water Tank",
                            value: property.buildingComponents?.waterTankDescription,
                          },
                          {
                            label: "Landscaping",
                            value:
                              property.buildingComponents?.landscapingDescription,
                          },
                          {
                            label: "Soil Drainage",
                            value:
                              property.buildingComponents?.soilDrainageDescription,
                          },
                          {
                            label: "PVC Pipes",
                            value: property.buildingComponents?.pvcPipesDescription,
                          },
                          {
                            label: "Wash Taps",
                            value: property.buildingComponents?.washTapsDescription,
                          },
                          {
                            label: "Fixtures & Fittings",
                            value:
                              property.buildingComponents?.fixturesFittingsDescription,
                          },
                        ]
                          .filter((item) => item.value)
                          .map((item, idx) => (
                            <div key={idx} className="bg-gray-50 rounded-lg p-4">
                              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                                {item.label}
                              </h3>
                              <p className="text-sm text-gray-600 break-words">
                                {item.value}
                              </p>
                            </div>
                          ))}
                      </div>
                    </section>
                    {/* Building Description */}
                    <section>
                      <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                        <span className="mr-2">🏗️</span> Building Description
                      </h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h3 className="text-sm font-semibold text-gray-700 mb-1">
                            Roof Condition
                          </h3>
                          <p className="text-sm text-gray-600">
                            {property.mainBuilding?.roofCondition || "N/A"}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h3 className="text-sm font-semibold text-gray-700 mb-1">
                            Walls Condition
                          </h3>
                          <p className="text-sm text-gray-600">
                            {property.mainBuilding?.wallsCondition || "N/A"}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h3 className="text-sm font-semibold text-gray-700 mb-1">
                            Windows Condition
                          </h3>
                          <p className="text-sm text-gray-600">
                            {property.mainBuilding?.windowsCondition || "N/A"}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h3 className="text-sm font-semibold text-gray-700 mb-1">
                            Doors Condition
                          </h3>
                          <p className="text-sm text-gray-600">
                            {property.mainBuilding?.doorsCondition || "N/A"}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h3 className="text-sm font-semibold text-gray-700 mb-1">
                            Floor Condition
                          </h3>
                          <p className="text-sm text-gray-600">
                            {property.mainBuilding?.floorCondition || "N/A"}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h3 className="text-sm font-semibold text-gray-700 mb-1">
                            Roof Type
                          </h3>
                          <p className="text-sm text-gray-600">
                            {property.mainBuilding?.roofType || "N/A"}
                          </p>
                        </div>
                        <div className="space-y-3">
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h3 className="text-sm font-semibold text-gray-700 mb-1">
                              Bedrooms
                            </h3>
                            <p className="text-sm text-gray-600">
                              {property.mainBuilding?.bedrooms || 0}
                            </p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h3 className="text-sm font-semibold text-gray-700 mb-1">
                              Bathrooms
                            </h3>
                            <p className="text-sm text-gray-600">
                              {property.mainBuilding?.bathrooms || 0}
                            </p>
                          </div>
                        </div>
                      </div>
                    </section>
                    {/* Adjustments & Variables */}
                    <section>
                      <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                        <span className="mr-2">📊</span> Adjustments & Variables
                      </h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h3 className="text-sm font-semibold text-gray-700 mb-1">
                            Year of Construction
                          </h3>
                          <p className="text-sm text-gray-600">
                            {property.mainBuilding?.constructionYear || 0}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h3 className="text-sm font-semibold text-gray-700 mb-1">
                            Effective Age (years)
                          </h3>
                          <p className="text-sm text-gray-600">
                            {new Date().getFullYear() -
                              (property.mainBuilding?.constructionYear || 0)}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h3 className="text-sm font-semibold text-gray-700 mb-1">
                            Annual Depreciation Rate (%)
                          </h3>
                          <p className="text-sm text-gray-600">
                            {property.valuationDetails?.annualDepreciationRate ||
                              0}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h3 className="text-sm font-semibold text-gray-700 mb-1">
                            Unit Price
                          </h3>
                          <p className="text-sm text-gray-600">
                            {property.valuationDetails?.mainHouseRate || 0}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h3 className="text-sm font-semibold text-gray-700 mb-1">
                            Constructed Area (m²)
                          </h3>
                          <p className="text-sm text-gray-600">
                            {property.mainBuilding?.constructedArea || 0}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h3 className="text-sm font-semibold text-gray-700 mb-1">
                            Original Cost of Construction
                          </h3>
                          <p className="text-sm text-gray-600">
                            {property.valuationDetails
                              ?.originalCostOfConstruction || 0}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h3 className="text-sm font-semibold text-gray-700 mb-1">
                            Maintenance Coefficient
                          </h3>
                          <p className="text-sm text-gray-600">
                            {property.valuationDetails?.maintenanceCoefficient ||
                              0}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h3 className="text-sm font-semibold text-gray-700 mb-1">
                            Depreciated Value (RWF)
                          </h3>
                          <p className="text-sm text-gray-600">
                            {property.valuationDetails?.depreciatedValue || 0}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h3 className="text-sm font-semibold text-gray-700 mb-1">
                            Present Value (RWF)
                          </h3>
                          <p className="text-sm text-gray-600">
                            {property.valuationDetails?.presentValue || 0}
                          </p>
                        </div>
                      </div>
                    </section>
                  </div>
                )}

              {/* Page 4: Valuation Measurements & Rates */}
              {currentPage === 3 && property.valuationDetails && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {propertyImages.map((image, index) => (
                      <div
                        key={index}
                        className="bg-white rounded-lg shadow-sm border p-2 cursor-pointer"
                        onClick={() => setZoomImage(image.url)}
                      >
                        <div className="aspect-square w-full rounded overflow-hidden">
                          <img
                            src={image.url}
                            alt={image.label}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <p className="text-center text-gray-700 mt-2 text-sm font-medium">
                          {image.label}
                        </p>
                      </div>
                    ))}
                  </div>
                  {/* Fullscreen Zoom Modal */}
                  {zoomImage && (
                    <div
                      className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
                      onClick={() => setZoomImage(null)}
                    >
                      <img
                        src={zoomImage}
                        alt="Zoomed"
                        className="max-w-full max-h-full object-contain"
                      />
                      <button
                        className="absolute top-4 right-4 text-white text-4xl font-bold"
                        onClick={() => setZoomImage(null)}
                      >
                        ×
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* Page 5: Financial Analysis & Summary */}
              {currentPage === 4 && property.valuationDetails && (
                <>
                  {/* Property location on map*/}
                  {property.mainBuilding && (
                    <div className="mb-6">
                      <h3 className="text-base font-medium text-gray-800 mb-3">
                        🗺️ Property Location
                      </h3>
                      <PropertyMap
                        Upi={property.upi || ""}
                        location={[
                          property?.location?.coordinates?.latitude || 1.88596,
                          property?.location?.coordinates?.longitude ||
                            30.129675,
                        ]}
                      />
                      <div className="grid grid-cols-1 gap-4 md:gap-8 mt-6">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">
                            Google Maps View
                          </h4>
                          <iframe
                            src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.979567234864!2d${
                              property?.location?.coordinates?.longitude || 0
                            }!3d${
                              property?.location?.coordinates?.latitude || 0
                            }!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjM6MTM0!5e0!3m2!1sen!2sng!4v1680879000000!5m2!1sen!2sng`}
                            width="100%"
                            height="400"
                            style={{ border: 0, borderRadius: "8px" }}
                            allowFullScreen={false}
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                          />
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-2">
                            Kigali City Masterplan
                          </h4>
                          <iframe
                            src="https://masterplan.kigalicity.gov.rw/portal/apps/webappviewer/index.html?id=fdd2e30cbc15401d9daee5f68982d755"
                            width="100%"
                            height="400"
                            style={{ border: 0, borderRadius: "8px" }}
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            title="Kigali City Masterplan"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Property Details */}
                  <section>
                    <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                      <span className="mr-2">📋</span> Property Details
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-gray-700 mb-1">
                          Area (sqft)
                        </h3>
                        <p className="text-lg font-medium text-gray-900">
                          {property.landParcel?.area
                            ? (property.landParcel.area * 10.7639).toFixed(0)
                            : 0}
                        </p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-gray-700 mb-1">
                          Age (years)
                        </h3>
                        <p className="text-lg font-medium text-gray-900">
                          {new Date().getFullYear() -
                            (property.mainBuilding?.constructionYear || 0)}
                        </p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-gray-700 mb-1">
                          Condition (1-10)
                        </h3>
                        <div className="flex items-center">
                          <p className="text-lg font-medium text-gray-900 mr-3">
                            {(() => {
                              const conditionMap: Record<string, number> = {
                                Excellent: 10,
                                Good: 7,
                                Average: 5,
                                Poor: 2,
                              };
                              return (
                                conditionMap[
                                  property.mainBuilding?.maintenanceCondition ||
                                    ""
                                ] || 0
                              );
                            })()}
                          </p>
                          <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-green-500"
                              style={{
                                width: `${
                                  ([
                                    "Excellent",
                                    "Good",
                                    "Average",
                                    "Poor",
                                  ].indexOf(
                                    property.mainBuilding?.maintenanceCondition ||
                                      ""
                                  ) + 1) * 25
                                }%`,
                              }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h3 className="text-sm font-semibold text-gray-700 mb-1">
                          Zoning
                        </h3>
                        <p className="text-gray-600">
                          {property.landParcel?.landUse || "N/A"}
                        </p>
                      </div>
                    </div>
                  </section>
                  {/* Comparative Analysis */}
                  <section>
                    <h2 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                      <span className="mr-2">📊</span> Comparative Analysis
                    </h2>
                    <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Property
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Location
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Area
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Price
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                              Adjustments
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          <tr className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              Subject Property
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {property.location?.district},{" "}
                              {property.location?.sector}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {property.landParcel?.area || 0} m²
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              RWF{" "}
                              {property.valuationDetails?.totalPropertyValue.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              N/A
                            </td>
                          </tr>
                          <tr className="bg-blue-50">
                            <td
                              className="px-4 py-3 text-sm font-medium text-blue-900"
                              colSpan={3}
                            >
                              Estimated Value
                            </td>
                            <td className="px-4 py-3 text-lg font-bold text-blue-900">
                              RWF{" "}
                              {property.valuationDetails?.totalPropertyValue.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-blue-600">
                              Market Adjusted
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-4 bg-gray-50 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">
                        Summary
                      </h3>
                      <p className="text-sm text-gray-600">
                        Based on property data, the total value is estimated at{" "}
                        <span className="font-semibold">
                          RWF{" "}
                          {property.valuationDetails?.totalPropertyValue.toLocaleString()}
                        </span>
                        .
                      </p>
                    </div>
                  </section>
                </>
              )}
            </div>

            {/* Pagination */}
            <div className="flex justify-between items-center mt-8 pt-6 border-t">
              <button
                onClick={prevPage}
                disabled={currentPage === 1}
                className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700 transition"
              >
                <ChevronLeft size={16} className="mr-1" />
                Previous
              </button>
              <div className="flex items-center space-x-2">
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`w-8 h-8 rounded-full text-sm font-medium transition ${
                      currentPage === i + 1
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
              <button
                onClick={nextPage}
                disabled={currentPage === totalPages}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700 transition"
              >
                Next
                <ChevronRight size={16} className="ml-1" />
              </button>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="sticky top-6 space-y-6">
            <ValuationSidebarSuperVisor
              property={property}
              propertyId={propertyId}
              mode="supervisor" // or "collector" based on your logic
              onValuationComplete={() => {
                // Refresh property data or show success message
              }}
            />
            {/* Quick Stats Card */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Quick Stats
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Property Status</span>
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium ${
                      property.valuationDetails?.status === "Completed"
                        ? "bg-green-100 text-green-800"
                        : property.valuationDetails?.status === "Pending"
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-blue-100 text-blue-800"
                    }`}
                  >
                    {property.valuationDetails?.status || "New"}
                  </span>
                </div>
                {property.valuationDetails?.assignedCollector && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Assigned To</span>
                    <span className="font-medium text-sm">
                      {property.valuationDetails.assignedCollector}
                    </span>
                  </div>
                )}
                {property.valuationDetails?.valuationDate && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">
                      Valuation Date
                    </span>
                    <span className="font-medium text-sm">
                      {new Date(
                        property.valuationDetails.valuationDate
                      ).toLocaleDateString()}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Total Images</span>
                  <span className="font-medium text-sm">
                    {propertyImages.length}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {isLightboxOpen && hasImages && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90">
          <div className="relative w-full h-full flex flex-col items-center justify-center">
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-10 text-white p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <X size={24} />
            </button>
            <div className="absolute top-4 left-4 z-10 flex items-center space-x-2">
              <button
                onClick={handleZoomOut}
                disabled={zoomLevel <= 0.5}
                className="text-white p-2 hover:bg-white/10 rounded-full disabled:opacity-50 transition-colors"
              >
                <ZoomOut size={20} />
              </button>
              <span className="text-white text-sm">
                {(zoomLevel * 100).toFixed(0)}%
              </span>
              <button
                onClick={handleZoomIn}
                disabled={zoomLevel >= 3}
                className="text-white p-2 hover:bg-white/10 rounded-full disabled:opacity-50 transition-colors"
              >
                <ZoomIn size={20} />
              </button>
            </div>
            <button
              onClick={prevImage}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 text-white p-3 hover:bg-white/10 rounded-full transition-colors"
            >
              <ChevronLeft size={24} />
            </button>
            <div className="max-w-4xl max-h-[80vh] overflow-auto">
              <img
                src={propertyImages[selectedImageIndex].url}
                alt={propertyImages[selectedImageIndex].label}
                className="transition-transform duration-200"
                style={{ transform: `scale(${zoomLevel})` }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src =
                    "https://via.placeholder.com/800x600/cccccc/969696?text=Image+Not+Found";
                }}
              />
            </div>
            <button
              onClick={nextImage}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 text-white p-3 hover:bg-white/10 rounded-full transition-colors"
            >
              <ChevronRight size={24} />
            </button>
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-lg font-medium">
              {propertyImages[selectedImageIndex].label}
            </div>
            <div className="absolute bottom-4 left-0 right-0 px-8">
              <div className="flex overflow-x-auto space-x-2 justify-center py-2">
                {propertyImages.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setSelectedImageIndex(index);
                      setZoomLevel(1);
                    }}
                    className={`flex-shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition-all ${
                      index === selectedImageIndex
                        ? "border-blue-500"
                        : "border-transparent hover:border-gray-300"
                    }`}
                  >
                    <img
                      src={image.url}
                      alt={image.label}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
            <div className="absolute top-4 right-16 text-white text-sm">
              {selectedImageIndex + 1} / {propertyImages.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyDetails;